import React, { ReactNode, useEffect, useState } from "react"
import styled from "styled-components"
import { themeGet } from "@styled-system/theme-get"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import Image from "@/common/components/NextImg"
import dynamic from "next/dynamic"
import Link from "next/link"
import ArticleHeader from "@/containers/Blog/article/ArticleHeader"
import Button from "@/common/components/Button"
import Container from "@/common/components/UI/Container"
const SectionLine = dynamic(() => import("@/common/components/_UI/SectionLine"))
const HotArticle = dynamic(() => import("@/containers/Blog/article/HotArticle"))
const HotTags = dynamic(() => import("@/containers/Blog/article/HotTags"))
const RecommendArticle = dynamic(
  () => import("@/containers/Blog/article/RecommendArticle")
)

interface ArticleProps {
  article: any
  articleAll: any
}

export default function Article({ article, articleAll }: ArticleProps) {
  const [blogContent, setBlogContent] = useState<ReactNode>([])

  const __content = article?.data[0]?.attributes.content
  // video轉換tag 前面
  const __content_for_iframeS = __content.replace(
    /iframeS/g,
    "<div className='videoBox'><iframe"
  )
  // video轉換tag 後面
  const __content_for_iframeE = __content_for_iframeS.replace(
    /iframeE/g,
    "></iframe></div>"
  )
  // code轉換tag 前面

  const __content_for_codeS = __content_for_iframeE.replace(
    /<pre>/g,
    "<pre className='prettyprint'>"
  )
  // code轉換完成
  const content = __content_for_codeS

  const { locale } = useRouter()
  const { t } = useTranslation("common")
  let contentType = "markdown"

  if (
    content.includes("<h2") ||
    content.includes("<p") ||
    content.includes("<br") ||
    content.includes("<span") ||
    content.includes("<iframe")
  ) {
    contentType = "html"
  }
  useEffect(() => {
    contentType === "html"
      ? import("html-react-parser")
          .then(mod => mod.default(content))
          .then(setBlogContent)
      : import("react-markdown")
          .then(mod => mod.default)
          .then(ReactMarkdown => (
            <ReactMarkdown>
              {article?.data[0]?.attributes.content}
            </ReactMarkdown>
          ))
          .then(setBlogContent)
  }, [content, contentType, article?.data])

  return (
    <Container>
      <ArticleBlock>
        <ContentBlock>
          <ArticleHeader shareTitle={article?.data[0]?.attributes?.title} />
          <TitleWrapper>
            <h1>{article?.data[0]?.attributes?.title}</h1>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px"
              }}
            >
              <div className="tags">
                {article?.data[0]?.attributes?.tags?.data?.map(
                  ({ attributes }: any) => (
                    <span key={attributes.name} className="tag">
                      #{attributes.name}
                    </span>
                  )
                )}
              </div>
              <div className="date">{article?.data[0]?.attributes?.date}</div>
            </div>
          </TitleWrapper>
          <ImageWrapper>
            <Image
              style={{ borderRadius: 8 }}
              alt="image-cover"
              src={
                article?.data[0]?.attributes?.cover_image?.data?.attributes.url
              }
              priority
            />
          </ImageWrapper>
          <ArticleWrapper>
            <ArticleContent className="articleContent">
              {blogContent}
            </ArticleContent>
          </ArticleWrapper>
          <SectionLine />
          <RecommendArticle
            data={articleAll.data}
            articleName={article?.data[0]?.attributes?.title}
            tagData={article?.data[0]?.attributes?.tags?.data}
          />
        </ContentBlock>
        <RightBlock>
          <HotArticle data={articleAll.data} />
          <HotTags />
        </RightBlock>
      </ArticleBlock>
      <BottomBlock>
        <ButtonBack>
          <Link href={`/${locale}/blog`} passHref legacyBehavior>
            <Button title={t("blog.back")} />
          </Link>
        </ButtonBack>
      </BottomBlock>
    </Container>
  )
}

const ArticleBlock = styled.article`
  flex: 1 1;
  padding-top: 160px;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 80px;
  align-items: flex-start;
  @media (max-width: 1024px) {
    flex-direction: column;
    padding-top: 100px;
  }
`

const ContentBlock = styled.div`
  flex: 3 1 100%;
  max-width: 1080px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 30px;
`

const RightBlock = styled.div`
  flex: 1 1;
  max-width: 400px;
  width: 100%;
  min-width: 300px;
  margin: 0 auto;
  position: sticky;
  top: 100px;
  display: flex;
  gap: 80px;
  flex-direction: column;

  padding-right: 16px;
  @media (max-width: 1024px) {
    padding-right: 0px;
  }
  @media (max-width: 768px) {
    min-width: 100%;
  }
`

const BottomBlock = styled.div`
  margin: 100px auto 50px;
  max-width: 1130px;
  display: flex;
  flex-direction: column;
  gap: 80px;
`

const ArticleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 1;
  justify-content: center;
  width: 100%;
  margin: 0 auto;
`
const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  h1 {
    margin: 0;
    font-size: 34px;
    background: linear-gradient(to right, #ffffff, #ffc211);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow:
      0 0 20px rgb(255 194 17 / 20%),
      0 0 60px rgb(255 194 17 / 50%);
    @media (max-width: 640px) {
      font-size: 24px;
    }
  }
  .tags {
    display: flex;
    gap: 12px;
  }
  .tag {
    color: #ffffff;
  }
  @media (max-width: 768px) {
    /* padding-top: 100px; */
    h2 {
      font-size: 24px;
    }
  }
`
const ArticleContent = styled.div`
  word-wrap: break-word;
  overflow: visible;
  width: 100%;
  padding-bottom: 30px !important;
  overflow: visible;

  h1 {
    font-family: "Noto Sans SC", "Noto Serif TC", Arial, sans-serif;
    font-family: "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
    font-weight: 700;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: rgba(255, 255, 255, 1);
    color: ${themeGet("colors.primary")};
    font-size: 2rem;
    min-width: 80%;
    display: flex;
    position: relative;
    margin-top: 60px;
    padding-top: 45px;
    margin-bottom: 2rem;
    letter-spacing: 1px;
    line-height: 2.5rem;
    border-top: 0.5px solid ${themeGet("colors.primary")}70;

    @media (max-width: 730px) {
      font-size: 1.5rem;
      margin-bottom: 0px;
      letter-spacing: 0px;
      line-height: 1.6rem;
      font-weight: 700;
      text-align: left;
    }
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: "Noto Sans SC", "Noto Serif TC", Arial, sans-serif;
    font-family: "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
    font-weight: 700;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: ${themeGet("colors.primary")};
    font-size: 1.5rem;
    margin-top: 69px;
    margin-bottom: 2rem;
    line-height: 33.18px;
    letter-spacing: 1px;
    overflow: visible;

    @media (max-width: 730px) {
      font-size: 1.2rem;
      margin-top: 40px;
      margin-bottom: 1rem;
      letter-spacing: 0px;
      line-height: 1.4rem;
      font-weight: 700;
      justify-content: flex-start;
      text-align: left;
    }
  }

  u {
    text-decoration: none;
  }

  u,
  a {
    color: rgba(255, 255, 255, 1);
    padding-bottom: 3px;
    font-weight: 300;
    opacity: 1;
    font-weight: 300;
    transition: 0.3s ease;
    border-bottom: 1px solid ${themeGet("colors.primary")};
    position: relative;
    background: linear-gradient(
        ${themeGet("colors.primary")}00 0%,
        ${themeGet("colors.primary")} 100%
      )
      no-repeat left 120%/100% 20%;

    &:hover {
      text-shadow: 0px 1px 8px black;

      background: linear-gradient(
          ${themeGet("colors.primary")}00,
          ${themeGet("colors.primary")}
        )
        no-repeat left 120%/100% 100%;

      &:before {
        opacity: 1;
      }
    }
  }
  strong {
    font-weight: 700;
    color: ${themeGet("colors.primary")};
  }
  figure {
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-end: 0;
    margin-inline-start: 0;
    width: 100% !important;
  }
  img {
    width: 100% !important;
    object-fit: contain;
    max-height: 90vh;
    margin: 37px auto 20px auto;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 8px;
    overflow: hidden;
  }

  figcaption {
    // background-color: #fff;
    font-size: 16px;
    text-align: center;
    margin-top: -5px;
  }

  blockquote {
    margin-top: 30px;
    display: block;
    padding-left: 20px;
    margin-left: -23px;
    padding-bottom: 5px;
    position: relative;
    margin-right: 0;

    &:before {
      content: "";
      position: absolute;
      border-left: 4px solid ${themeGet("colors.primary")};
      width: 52px;
      height: calc(100% + 16px);
      top: -8px;
      left: 0px;

      @media (max-width: 768px) {
        left: 0px;
      }
    }

    @media (max-width: 768px) {
      margin-left: 0px;
      padding-left: 20px;
    }
  }

  p {
    font-size: 1.25rem;
    font-family: "lora", "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
    font-family: "lora", "Noto serif SC", "Noto serif TC", Arial, sans-serif;
    letter-spacing: 0.05rem;
    line-height: 2rem;
    position: relative;
    font-weight: 300;
    color: rgba(255, 255, 255, 0.9);
    margin-top: 1.2rem;

    @media (max-width: 730px) {
      font-size: 1rem;
      margin-top: 1.5rem;
      margin-bottom: -8.28px;
      line-height: 28px;
      font-weight: 300;
    }
  }

  ul {
    list-style: none;
    padding-left: 0px;
    // margin-top: 42px;
    margin-bottom: 42px;

    li {
      font-size: 1.25rem;
      font-family: "lora", "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
      font-family: "lora", "Noto serif SC", "Noto serif TC", Arial, sans-serif;
      letter-spacing: 0.05rem;
      line-height: 2rem;
      position: relative;
      font-weight: 300;
      color: rgba(255, 255, 255, 0.9);
      margin-top: 1.2rem;
      margin-bottom: 40x;

      @media (max-width: 730px) {
        font-size: 1rem;
        margin-top: 1.2rem;
        margin-bottom: -8.28px;
        line-height: 28px;
        font-weight: 300;
      }

      margin: 40px 0 -9.2px 30px;

      @media (max-width: 730px) {
        margin: 40px 0 -9.2px 20px;
      }

      position: relative;

      &:before {
        content: "";
        position: absolute;
        display: inline-block;
        width: 0.4em;
        height: 0.4em;
        margin-left: -1rem;
        border-radius: 1em;
        background-color: rgba(255, 255, 255, 1);
        background-color: ${themeGet("colors.primary")};
        margin-right: 1rem;
        top: 15px;
        transform: translateY(-50%);
      }
    }
  }

  ol {
    padding-left: 0px;
    counter-reset: li;
    margin-bottom: 42px;

    li {
      font-size: 1.25rem;
      font-family: "lora", "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
      font-family: "lora", "Noto serif SC", "Noto serif TC", Arial, sans-serif;
      letter-spacing: 0.05rem;
      line-height: 2rem;
      position: relative;
      font-weight: 300;
      color: rgba(255, 255, 255, 0.9);
      margin-top: 1.2rem;
      margin-bottom: 40x;
      margin: 40px 0 -9.2px 30px;

      @media (max-width: 730px) {
        font-size: 1rem;
        margin-top: 1.2rem;
        margin-bottom: -8.28px;
        line-height: 28px;
        font-weight: 300;
      }

      @media (max-width: 730px) {
        margin: 40px 0 -9.2px 20px;
      }

      counter-increment: li;

      &:before {
        content: counter(li);
        display: inline-block;
        position: absolute;
        font-weight: 700;
        width: 1rem;
        height: 0.4em;
        margin-left: -1.2rem;
        border-radius: 1em;
        margin-right: 1rem;
        color: rgba(255, 255, 255, 1);
        color: ${themeGet("colors.primary")};
      }
    }
  }
  .videoBox {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 56.25%;
    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
  table {
    color: #fff;
    border-collapse: separate;
    width: 100%;
    border-spacing: 0;
    border: 0.5px solid ${themeGet("colors.primary")}90;
    border-radius: 16px;
    overflow: hidden;
    background: linear-gradient(-139deg, #03122e 0%, #001f58 100%);
    margin: 12px 0;

    tr {
      &:nth-child(1) {
        background: linear-gradient(139deg, #ffc211 0%, #ffc41167 100%);
        color: ${themeGet("colors.secondary")};
        font-weight: 700;
        td {
          border-right: 0.1px solid ${themeGet("colors.primary")};
          border-bottom: 0.1px solid ${themeGet("colors.primary")};
        }
      }
      &:nth-last-child(1) {
        border-bottom: 0px solid ${themeGet("colors.primary")};
        td {
          border-bottom: 0px solid ${themeGet("colors.primary")};
        }
      }
      td {
        text-align: center;
        border-right: 0.1px solid ${themeGet("colors.primary")}30;
        border-bottom: 0.1px solid ${themeGet("colors.primary")}30;
        padding: 10px;
        &:nth-child(1) {
          color: ${themeGet("colors.primary")};
          font-weight: 700;
        }
        &:nth-last-child(1) {
          border-right: 0px solid ${themeGet("colors.primary")};
        }
      }
    }
  }

  pre {
    background: linear-gradient(-139deg, #03122e 0%, #001f58 100%) !important;
    border-radius: 12px;
    padding: 12px !important;
    border: 0.5px solid ${themeGet("colors.primary")}90 !important;
    overflow: auto;
    ::-webkit-scrollbar {
      height: 2px;
      width: 2px;
    }
    span {
      word-wrap: break-word;
    }
    .str {
      color: #ec7600;
    }
    .kwd {
      color: #93c763;
    }
    .com {
      color: #66747b;
    }
    .typ {
      color: #678cb1;
    }
    .lit {
      color: #facd22;
    }
    .pun {
      color: #f1f2f3;
    }
    .pln {
      color: #f1f2f3;
    }
    .tag {
      color: #8ac763;
    }
    .atn {
      color: #e0e2e4;
    }
    .atv {
      color: #ec7600;
    }
    .dec {
      color: purple;
    }
    pre.prettyprint {
      border: 0px solid #888;
    }
    ol.linenums {
      margin-top: 0;
      margin-bottom: 0;
    }
    .prettyprint {
      background: #000;
    }
    li.L0,
    li.L1,
    li.L2,
    li.L3,
    li.L4,
    li.L5,
    li.L6,
    li.L7,
    li.L8,
    li.L9 {
      color: #555;
      list-style-type: decimal;
    }
    li.L1,
    li.L3,
    li.L5,
    li.L7,
    li.L9 {
      background: #111;
    }
    @media print {
      .str {
        color: #060;
      }
      .kwd {
        color: #006;
        font-weight: bold;
      }
      .com {
        color: #600;
        font-style: italic;
      }
      .typ {
        color: #404;
        font-weight: bold;
      }
      .lit {
        color: #044;
      }
      .pun {
        color: #440;
      }
      .pln {
        color: #000;
      }
      .tag {
        color: #006;
        font-weight: bold;
      }
      .atn {
        color: #404;
      }
      .atv {
        color: #060;
      }
    }
  }
`
const ImageWrapper = styled.div`
  position: relative;
  max-width: 1500px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
`
const ButtonBack = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`
